// Editor de regra de desembolso (espec. 8.3). Offcanvas prop-driven no padrão do
// `DisbursementCoverageOffcanvas`, com o que a espec. 8.5 pede por cima: `role="dialog"`,
// foco que entra ao abrir e volta ao fechar, e fechamento por Escape.
//
// Este arquivo é a máquina de estado: alvo, atalho, formulário, linhas, validação e
// salvar. A apresentação está em três irmãos — `RuleTargetPicker` (passo 1, mais o
// contrato do alvo), `RuleShortcutFields` e `RuleProfileTable` (passo 2).
//
// Passo 3 (Task 17): a pré-visualização é LOCAL (`RulePreviewChart` + `previewTargetCurve`)
// e o salvar fala com a rota por aqui — a menos que o chamador passe `onSave`, caso em
// que o editor só delega (contrato da Task 16, para quem já grava por conta).
import { useEffect, useMemo, useRef, useState } from "react";
import { isPresentationProject } from "@utils/presentationMode";

import DescribeRuleWithAi from "./ai/DescribeRuleWithAi";
import { confirmRuleWithAi, newIdempotencyKey } from "./ai/disbursementAiApi";
import { allocateCents } from "./allocate";
import {
  fetchDisbursementTargets,
  removeDisbursementRule,
  saveDisbursementRule,
} from "./disbursementApi";
import CompetenceInput from "./CompetenceInput";
import { EDITOR_MODES } from "./editorModes";
import { disbursementErrorDetail } from "./errorMessages";
import { competenceText } from "./format";
import { monthsBetween, monthsText, reais } from "./monthEdit";
import {
  MAX_LINES,
  MAX_OFFSET,
  ProfileError,
  WEIGHTED_MAX_OFFSET,
  describeProfile,
  validateProfile,
} from "./profile";
import ReferenceInstallmentsTable from "./ReferenceInstallmentsTable";
import {
  addLine,
  fitsCustom,
  lineMonth,
  linesProblem,
  linesTotal,
  postponeLine,
  referenceLines,
  referenceOf,
  referenceSaveOf,
  sameAsReference,
  weightedLines,
} from "./referenceSchedule";
import { ORIGIN_FOLLOWS_OBJECT, ORIGIN_NAMES } from "./OriginSelector";
import RuleImpactSection from "./RuleImpactSection";
import RuleOverridesSection, {
  EMPTY_OVERRIDE_DECISIONS,
  overridePlan,
  pendingDecisionText,
  resultingSeriesOf,
} from "./RuleOverridesSection";
import RulePreviewChart from "./RulePreviewChart";
import RuleProfileTable, {
  PROFILE_ERROR_ID,
  seedLines,
  withLineId,
} from "./RuleProfileTable";
import RuleShortcutFields, { REFERENCE_SHORTCUTS, buildShortcutProfile } from "./RuleShortcutFields";
import ReferenceMonthsTable from "./ReferenceMonthsTable";
import useRuleImpact, { IMPACT_DEBOUNCE_MS, requiresImpactPreview } from "./useRuleImpact";
import RuleTargetPicker, {
  TargetValue,
  normalizeTarget,
  ruleTargetLevel,
  ruleTargetOf,
  targetQueryLevel,
} from "./RuleTargetPicker";

// O contrato do alvo mora no `RuleTargetPicker`, mas a Task 17 o consome pelo editor.
export { normalizeTarget, ruleTargetLevel, ruleTargetOf, targetQueryLevel };

const SEARCH_DEBOUNCE_MS = 350;
const SAVE_ERROR_TEXT = "Não foi possível salvar a regra. Tente novamente.";
const SAVE_ERROR_PREFIX = "Não foi possível salvar a regra.";
// A obra mudou entre a prévia de impacto e a confirmação: nada foi gravado e a prévia é
// recalculada sozinha, para o cliente confirmar sobre o que de fato vai acontecer.
const STALE_PREVIEW_TEXT =
  "A obra mudou depois da prévia de impacto. A prévia foi recalculada; confira o novo impacto antes de reprogramar.";

// `conflictingGroup` chega como objeto `{ groupId, name }` (contrato da Task 13) — a
// string crua é só a forma tolerada. Interpolar o objeto direto imprimiria
// "[object Object]" na cara do cliente.
const conflictingGroupName = (group) =>
  typeof group === "string" ? group : group?.name;

const overlapText = (group) => {
  const name = conflictingGroupName(group);
  return name
    ? `Este alvo já está coberto pelo grupo "${name}".`
    : "Este alvo já está coberto por outro grupo.";
};

const saveErrorText = (result) => {
  const errorCode = result?.errorCode;
  if (errorCode === "impact_preview_stale") return STALE_PREVIEW_TEXT;
  if (errorCode === "group_overlap") {
    return `${SAVE_ERROR_PREFIX} ${overlapText(result?.conflictingGroup)}`;
  }
  // Vocabulário do `rulePlanner`/`rulesStore`: `invalid_target` e `rule_not_found`.
  // Não existe `target_invalid`/`target_not_found` no back-end — mapear aqueles
  // deixaria o cliente com a frase genérica justamente nos erros de alvo.
  const detail = disbursementErrorDetail(errorCode);
  return detail ? `${SAVE_ERROR_PREFIX} ${detail}` : SAVE_ERROR_TEXT;
};

// Os dois modos de um alvo da EAP (D20); reexportado para quem já importa o editor.
export { EDITOR_MODES };

const REMOVE_ERROR_TEXT = "Não foi possível remover a regra. Tente novamente.";

/** A competência de hoje ("AAAA-MM"): o início padrão de um alvo sem mês na origem (D25). */
const todayCompetence = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const DEFAULT_FORM = Object.freeze({
  unit: "months",
  installments: 1,
  startOffset: 0,
  downPaymentBps: 3000,
  downPaymentOffset: -1,
});

// O formulário dos atalhos no ajuste mês a mês (D21) abre com a janela da referência:
// "Parcelas iguais" cobre os meses dela a partir do início, e "Entrada + parcelas" põe a
// entrada no início e as parcelas nos meses seguintes.
const referenceForm = (shortcut, span) => (shortcut === "down_payment"
  ? { ...DEFAULT_FORM, downPaymentOffset: 0, installments: Math.max(1, span - 1), startOffset: 1 }
  : { ...DEFAULT_FORM, installments: Math.max(1, span), startOffset: 0 });

// As parcelas de um perfil em bps gravado no ajuste mês a mês, em centavos sobre o total de
// hoje. Só em meses: uma forma em dias não vira linha por competência.
const bpsLinesAsCents = (profile, totalCents) => {
  if (!Array.isArray(profile) || profile.some((line) => line?.offsetMonths === undefined)) return null;
  const cents = allocateCents(totalCents, profile.map((line) => line.percentBps));
  return profile.map((line, index) => ({ offsetMonths: line.offsetMonths, weight: cents[index] }))
    .filter((line) => line.weight > 0);
};

const profileErrorText = (error) =>
  error instanceof ProfileError
    ? error.message
    : "Forma de desembolso inválida";

const RuleEditorOffcanvas = ({
  open,
  onClose,
  project = null,
  projectId = null,
  target = null,
  initialTarget = null,
  profile = null,
  initialRule = null,
  initialSource = "manual",
  rulesByRuleId = null,
  readOnly = false,
  onChangeTarget,
  onChangeProfile,
  onSave,
  onSaved,
  // A origem do desembolso da obra (`state.origin`): nomeia a série de comparação da
  // pré-visualização e a origem vigente no cabeçalho do alvo.
  origin = null,
  impactDebounceMs = IMPACT_DEBOUNCE_MS,
  // Abre a regra de um insumo/grupo que governa parte do alvo (lista de exceções).
  onEditOverride,
  // Abrir já no ajuste mês a mês (pelos ajustes de mês de um alvo). Uma regra com base na
  // referência (D21) abre nele de qualquer jeito.
  // `null` = automático: com dados programados, o cronograma atual; sem nada, o novo cronograma.
  initialMode = null,
}) => {
  const workId = projectId ?? project?._id ?? null;
  const seededTarget = useMemo(
    () => normalizeTarget(target ?? initialTarget),
    [target, initialTarget],
  );
  // D21: a regra com base na referência é o ajuste mês a mês; o perfil dela (ausente ou por
  // peso) não semeia o novo cronograma.
  const referenceRule = initialRule?.basis === "reference" ? initialRule : null;
  const seededProfile = profile ?? (referenceRule ? null : initialRule?.profile) ?? null;
  // Revisão de 2026-10-07: um alvo de EAP que já tem regra abre no cronograma atual (o que a
  // regra produz hoje), ajustando os meses.
  const seededMode = referenceRule || initialRule?.target?.kind === "node" ? EDITOR_MODES.monthEdit : initialMode;

  const [targetType, setTargetType] = useState(
    (target ?? initialTarget)?.kind === "node" ? "node" : "erp_group",
  );
  const [nodeLevel, setNodeLevel] = useState("substep");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [targetsError, setTargetsError] = useState(false);
  const [chosenTarget, setChosenTarget] = useState(seededTarget);
  // Semear a partir de uma regra existente cai em "Personalizado": salvar de novo sem
  // tocar em nada tem que preservar exatamente o perfil gravado, não regenerá-lo.
  const [shortcut, setShortcut] = useState(seededProfile ? "custom" : "equal");
  const [form, setForm] = useState(DEFAULT_FORM);
  // Revisão de 2026-10-07: enquanto o cliente não mexe nos campos do atalho, o novo
  // cronograma é UNIFORME — parcelas iguais, uma por mês da janela do alvo na origem.
  const [formTouched, setFormTouched] = useState(false);
  const [customLines, setCustomLines] = useState(() =>
    seedLines(seededProfile),
  );
  const [description, setDescription] = useState(
    initialRule?.description ?? "",
  );
  // Início do novo cronograma ("YYYY-MM"). Vazio = o primeiro mês do alvo na origem, que é
  // o que o motor usa para uma regra sem início; o que vai gravado é sempre o efetivo.
  const [startMonth, setStartMonth] = useState(initialRule?.startMonth ?? "");
  const [descriptionDirty, setDescriptionDirty] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const confirmationRequest = useRef(null);
  const confirmedResult = useRef(null);
  // Origem do perfil gravado (espec. 10.3): a IA preenche com `"ai_assistant"`, e
  // QUALQUER edição manual depois — atalho, linha, campo do atalho — volta para
  // `"manual"`. Sem isso o rótulo de origem na lista mentiria sobre um perfil que o
  // cliente reescreveu depois da proposta.
  const [ruleSource, setRuleSource] = useState(initialSource);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Série do previsto buscada quando o alvo chega SEM ela (linha da lista de regras
  // não carrega `plannedSeries`): guardada junto com a chave do alvo, pra nunca
  // vazar o previsto de um alvo para outro, e com o `status` da busca
  // (`loading`/`ready`/`failed`) — "ainda não chegou" e "não tem" são coisas
  // diferentes na tela.
  const [hydrated, setHydrated] = useState(null);
  const [overwriteWarning, setOverwriteWarning] = useState(false);
  // D19/D5/D6: com regras mais específicas dentro do alvo, o usuário escolhe por grupo — EAP
  // (`keep` = preservar, `override` = sobrescrever) e insumo/grupo (`keep`, `override` =
  // aplicar, `both` = as duas). `null` no grupo = não escolheu.
  const [overrideDecisions, setOverrideDecisions] = useState(EMPTY_OVERRIDE_DECISIONS);
  // Cronograma novo (regra) ou ajuste mês a mês. Só um alvo de EAP tem os dois.
  const [mode, setMode] = useState(seededMode);
  // D21: o ajuste mês a mês sobre a referência — atalho, formulário dos atalhos, parcelas do
  // "Personalizado" (semeadas da referência quando ela chega: `refSeed` é a chave dela) e o
  // início ("" = o primeiro mês da referência).
  const [refShortcut, setRefShortcut] = useState("months");
  const [refForm, setRefForm] = useState(DEFAULT_FORM);
  const [refLines, setRefLines] = useState(null);
  const [refSeed, setRefSeed] = useState(null);
  const [refStart, setRefStart] = useState(referenceRule?.startMonth ?? "");
  // D22: "Remover regra" no rodapé, com confirmação.
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  // A identidade do alvo semeado é a CHAVE, não o objeto: um pai que remonta a prop a
  // cada render (ou recarrega o mesmo alvo da rota) não pode resetar o editor.
  const seedKey = JSON.stringify([
    workId,
    seededTarget?.key ?? null,
    seededProfile,
    initialRule?.ruleId ?? null,
    initialSource,
    seededMode,
    open,
  ]);
  const lastSeededKey = useRef(seedKey);
  const saveScope = useRef(0);
  const renderedSeed = useRef(seedKey);
  if (renderedSeed.current !== seedKey) {
    renderedSeed.current = seedKey;
    saveScope.current += 1;
  }
  useEffect(
    () => () => {
      saveScope.current += 1;
    },
    [],
  );
  // Chave cuja busca de previsto já foi disparada. É um ref, não estado: se o estado
  // `hydrated` entrasse nas dependências do efeito, marcar "carregando" o re-rodaria
  // e a limpeza cancelaria a própria busca que acabou de sair.
  const hydrationRequest = useRef(null);

  const queryLevel = targetType === "node" ? nodeLevel : targetType;

  // Alvo controlado pelo chamador: quando a chave da prop muda, o editor segue.
  useEffect(() => {
    const key = seedKey;
    if (lastSeededKey.current === key) return;
    lastSeededKey.current = key;
    setChosenTarget(seededTarget);
    // Alvo semeado NOVO leva o resto do formulário com ele: editar uma regra pela
    // lista (`onEditRule`) cai em "Personalizado" com o perfil GRAVADO, e abrir para
    // um alvo sem regra volta ao padrão — nunca sobra a forma do alvo anterior.
    setShortcut(seededProfile ? "custom" : "equal");
    setCustomLines(seedLines(seededProfile));
    setForm(DEFAULT_FORM);
    setFormTouched(false);
    setDescription(initialRule?.description ?? "");
    setStartMonth(initialRule?.startMonth ?? "");
    setDescriptionDirty(false);
    setAiDraft(null);
    setSaving(false);
    confirmationRequest.current = null;
    confirmedResult.current = null;
    setOverwriteWarning(false);
    setOverrideDecisions(EMPTY_OVERRIDE_DECISIONS);
    setMode(seededMode);
    setRefShortcut("months");
    setRefForm(DEFAULT_FORM);
    setRefLines(null);
    setRefSeed(null);
    setRefStart(referenceRule?.startMonth ?? "");
    setConfirmingRemove(false);
    setRemoving(false);
    setSaveError(null);
    setHydrated(null);
    setRuleSource(initialSource);
    hydrationRequest.current = null;
  }, [seedKey, seededTarget, seededProfile, initialRule, referenceRule, initialSource, seededMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Foco entra no painel ao abrir e volta pro gatilho ao fechar (espec. 8.5).
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    panelRef.current?.focus();
    return () => {
      const previous = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (
        previous &&
        typeof previous.focus === "function" &&
        document.body.contains(previous)
      ) {
        previous.focus();
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [open, search]);

  useEffect(() => {
    if (!open || chosenTarget || !workId) return undefined;
    // `cancelled` é a guarda contra resposta desatualizada: trocar o filtro enquanto
    // uma busca voa não pode deixar a resposta antiga sobrepor a mais recente.
    let cancelled = false;
    (async () => {
      setLoadingTargets(true);
      setTargetsError(false);
      const payload = await fetchDisbursementTargets(workId, {
        level: queryLevel,
        search: debouncedSearch,
      });
      if (cancelled) return;
      if (!payload || !Array.isArray(payload.items)) {
        setCandidates([]);
        setTargetsError(true);
        setLoadingTargets(false);
        return;
      }
      setCandidates(payload.items.map(normalizeTarget));
      setLoadingTargets(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, chosenTarget, workId, queryLevel, debouncedSearch]);

  // Alvo sem previsto na mão: busca a série do próprio alvo antes de pré-visualizar.
  // Sem isso a pré-visualização de uma regra existente sairia zerada — e um zero na
  // tela o cliente lê como resposta do motor, não como dado que faltou.
  useEffect(() => {
    if (!open || !workId || !chosenTarget) return undefined;
    if ((chosenTarget.plannedSeries?.length ?? 0) > 0) return undefined;
    const requestKey = JSON.stringify([workId, chosenTarget.key]);
    if (hydrationRequest.current === requestKey) return undefined;
    // `?level=` é OBRIGATÓRIO na rota de alvos (400 `invalid_target_level` sem ele) e
    // não aceita o nível de armazenamento `resource_group`: o nível de consulta é
    // derivado AQUI, na hora do fetch. Sem nível derivável não há consulta possível —
    // o alvo fica com a série que ele já tem (vazia) e o gráfico dá o veredito
    // honesto, em vez de gastar um 400 que morreria calado.
    const queryLevelForTarget = targetQueryLevel(chosenTarget);
    if (!queryLevelForTarget) {
      setHydrated({
        key: chosenTarget.key,
        series: chosenTarget.plannedSeries ?? [],
        status: "ready",
      });
      return undefined;
    }
    hydrationRequest.current = requestKey;
    let cancelled = false;
    setHydrated({ key: chosenTarget.key, series: [], status: "loading" });
    (async () => {
      const payload = await fetchDisbursementTargets(workId, {
        level: queryLevelForTarget,
        search:
          chosenTarget.kind === "node"
            ? chosenTarget.key.slice("node:".length, "node:".length + 80)
            : String(chosenTarget.label ?? "").slice(0, 80),
      });
      if (cancelled) return;
      if (!payload || !Array.isArray(payload.items)) {
        // Envelope de erro (ou `null` de rede) é FALHA, não "alvo sem previsto".
        setHydrated({ key: chosenTarget.key, series: [], status: "failed" });
        return;
      }
      const match = payload.items
        .map(normalizeTarget)
        .find((candidate) => candidate.key === chosenTarget.key);
      setHydrated({
        workId,
        key: chosenTarget.key,
        series: match?.plannedSeries ?? [],
        plannedCents: match?.plannedCents ?? null,
        unscheduledCents: match?.unscheduledCents ?? 0,
        status: "ready",
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workId, chosenTarget]);

  const previewSeries = useMemo(() => {
    if ((chosenTarget?.plannedSeries?.length ?? 0) > 0)
      return chosenTarget.plannedSeries;
    return hydrated?.key && hydrated.key === chosenTarget?.key
      ? hydrated.series
      : [];
  }, [chosenTarget, hydrated]);

  // Sem obra não há o que buscar: cai direto no veredito honesto do gráfico em vez
  // de girar "carregando" para sempre.
  const previewHydration = useMemo(() => {
    if (!chosenTarget || !workId) return "ready";
    if ((chosenTarget.plannedSeries?.length ?? 0) > 0) return "ready";
    return hydrated?.key === chosenTarget.key ? hydrated.status : "loading";
  }, [chosenTarget, workId, hydrated]);

  // O primeiro mês com valor do alvo na origem: o início padrão do novo cronograma.
  const firstOriginMonth = useMemo(
    () =>
      (previewSeries ?? [])
        .filter((point) => point?.cents > 0)
        .map((point) => point?.month ?? point?.competence ?? null)
        .filter(Boolean)
        .sort()[0] ?? null,
    [previewSeries],
  );
  // A janela do alvo na origem, em meses (do primeiro ao último com valor): o número de
  // parcelas do novo cronograma uniforme, até o que a forma aceita a partir do início.
  const originWindowMonths = useMemo(() => {
    const months = (previewSeries ?? [])
      .filter((point) => point?.cents > 0)
      .map((point) => point?.month ?? point?.competence ?? null)
      .filter(Boolean)
      .sort();
    if (months.length === 0) return 0;
    return Math.min(monthsBetween(months[0], months[months.length - 1]).length, MAX_OFFSET + 1);
  }, [previewSeries]);
  const scheduleForm = formTouched || originWindowMonths === 0
    ? form
    : { ...form, installments: originWindowMonths };
  // D25: o orçamento do alvo que ainda não tem distribuição; o novo cronograma o agenda do
  // zero, a partir do início (sem mês na origem, o padrão é o mês atual).
  const targetUnscheduled = (() => {
    if (!chosenTarget) return 0;
    if (Number.isSafeInteger(chosenTarget.unscheduledCents)) return chosenTarget.unscheduledCents;
    return hydrated?.key === chosenTarget.key && Number.isSafeInteger(hydrated.unscheduledCents)
      ? hydrated.unscheduledCents
      : 0;
  })();
  const effectiveStartMonth = startMonth || firstOriginMonth
    || (targetUnscheduled > 0 ? todayCompetence() : null);

  // O perfil visível é SEMPRE a tabela: os atalhos só a preenchem (espec. 8.3). Assim o
  // cliente vê exatamente o que vai ser gravado, e o "Personalizado" é a mesma tabela.
  const visible = useMemo(() => {
    if (shortcut === "custom") return { lines: customLines, error: null };
    try {
      // Chaves determinísticas: `validateProfile` garante (unidade, defasagem) única no
      // gerado, então unidade + defasagem + atalho identifica a linha sem depender da
      // posição. Cada linha vira a forma interna uniforme `{ unit, offset, percentBps }`
      // (profile.js aceita `unit: "months"` com `offset` exatamente como `offsetMonths`),
      // então a tabela e o validador nunca precisam ramificar por forma.
      const lines = buildShortcutProfile(shortcut, scheduleForm).map((line) => {
        const unit = line.unit ?? "months";
        const offset = line.unit === undefined ? line.offsetMonths : line.offset;
        return { lineId: `${shortcut}:${unit}:${offset}`, unit, offset, percentBps: line.percentBps };
      });
      return { lines, error: null };
    } catch (error) {
      return { lines: [], error: profileErrorText(error) };
    }
  }, [shortcut, scheduleForm.installments, form, customLines]);

  // `validateProfile`/`describeProfile` lançam: o resumo só é pedido depois de validar.
  const validation = useMemo(() => {
    if (visible.error)
      return { profile: null, message: visible.error, summary: null };
    try {
      const validated = validateProfile(
        visible.lines.map(({ unit, offset, percentBps }) => ({ unit, offset, percentBps })),
      );
      return {
        profile: validated,
        message: null,
        summary: describeProfile(validated),
      };
    } catch (error) {
      return { profile: null, message: profileErrorText(error), summary: null };
    }
  }, [visible]);

  // Só notifica com perfil VÁLIDO e só com o editor aberto: um offcanvas fechado não
  // tem por que empurrar perfil semeado para a página que o hospeda.
  useEffect(() => {
    if (!open || !validation.profile) return;
    onChangeProfile?.(validation.profile);
  }, [open, validation.profile, onChangeProfile]);

  // Modo apresentação é gate duro: um `readOnly={false}` do chamador não o destrava.
  const isReadOnly = readOnly || isPresentationProject(project);

  // A prévia dos serviços impactados (design D13): pedida com a forma VÁLIDA, só para alvos
  // de insumo, família ou grupo, e nunca em modo apresentação. Fica antes do retorno do
  // editor fechado — um hook não pode depender de o painel estar aberto.
  const needsImpact = requiresImpactPreview(chosenTarget);
  // Num alvo de EAP a prévia não trava o salvar, mas é ela que lista os insumos com regra
  // própria dentro do serviço/subetapa/etapa — por isso é pedida também para nós.
  const wantsImpact = needsImpact || chosenTarget?.kind === "node";
  // O ajuste mês a mês só existe num alvo de EAP.
  const nodeTarget = chosenTarget?.kind === "node";
  // Somente leitura não edita nada: o modo do ajuste nem abre.
  // Modo automático (revisão de 2026-10-07): enquanto ninguém escolheu, um alvo de EAP com
  // dados programados (valor na origem, ou a origem ainda carregando) abre no cronograma
  // atual; sem nada programado (orçamento sem distribuição), no novo cronograma.
  const hasScheduledData = previewHydration === "loading"
    || (previewSeries ?? []).some((point) => point?.cents > 0);
  const effectiveMode = mode ?? (nodeTarget && hasScheduledData ? EDITOR_MODES.monthEdit : EDITOR_MODES.schedule);
  const monthMode = nodeTarget && effectiveMode === EDITOR_MODES.monthEdit && !isReadOnly;
  // D21: no ajuste mês a mês a prévia é pedida UMA vez, como deslocamento nulo sobre a
  // referência: o que importa dela aqui são os insumos com regra própria (D19), a origem
  // governada em cada cenário e o carimbo. As parcelas mudam sem ir ao servidor.
  const impact = useRuleImpact({
    workId,
    target: chosenTarget ? ruleTargetOf(chosenTarget) : null,
    profile: monthMode ? null : validation.profile,
    startMonth: monthMode ? null : effectiveStartMonth,
    basis: monthMode ? "reference" : "new",
    enabled: open && !isReadOnly && wantsImpact,
    debounceMs: impactDebounceMs,
  });
  const impactOverrides = impact.status === "ready" ? impact.answer?.overrides ?? [] : [];
  // As escolhas sobre as regras mais específicas, já traduzidas para o salvar e a prévia. A
  // proposta de IA confirma pela rota da IA, que não desativa regras: ali vale manter.
  const plan = overridePlan(impactOverrides, overrideDecisions, { canOverride: !aiDraft });
  // A regra ATIVA do próprio alvo, quando há: com ela, o cronograma atual parte do que a regra
  // produz hoje (a prévia devolve `currentSeries`), e não da origem.
  const ownRule = (() => {
    const candidates = [
      initialRule,
      chosenTarget?.ruleId ? rulesByRuleId?.[chosenTarget.ruleId] ?? null : null,
    ];
    return candidates.find((rule) => rule?.active !== false && rule?.target?.key === chosenTarget?.key) ?? null;
  })();
  const awaitingCurrent = monthMode && ownRule !== null && (impact.status === "loading" || impact.status === "idle");
  const currentFromRule = monthMode && ownRule !== null && impact.status === "ready" && impact.answer?.currentSeries
    ? (impactOverrides.length > 0 && plan.governsAll
      ? impact.answer.currentSeries.override ?? impact.answer.currentSeries.keep
      : impact.answer.currentSeries.keep)
    : null;
  const referenceIsCurrent = currentFromRule !== null;
  // A referência do ajuste: a origem do alvo; mantendo as regras dos insumos (D19), a origem
  // sem as partes delas, como o servidor a calculou.
  const keepOrigin = monthMode && !referenceIsCurrent && impactOverrides.length > 0
    && !plan.governsAll
    ? impact.answer?.originSeries?.keep ?? null
    : null;
  const reference = useMemo(
    () => referenceOf(currentFromRule ?? keepOrigin ?? previewSeries),
    [currentFromRule, keepOrigin, previewSeries],
  );
  const referenceKey = JSON.stringify([...reference.map]);
  // A origem INTEIRA do alvo: é o retrato gravado com o ajuste e o que a lista compara com o
  // valor que o alvo tem hoje para avisar que a referência mudou.
  const originReference = useMemo(() => referenceOf(previewSeries), [previewSeries]);
  // Semeia as parcelas quando a referência chega (ou muda de cenário): da regra gravada, na
  // primeira vez, ou da própria referência.
  useEffect(() => {
    if (!open || !monthMode || awaitingCurrent || reference.first === null || refSeed === referenceKey) return;
    const firstSeed = refSeed === null;
    setRefSeed(referenceKey);
    setRefForm(referenceForm("equal", reference.span));
    if (referenceIsCurrent) {
      // O cronograma atual já é o resultado da regra: as linhas são ele, a partir do 1º mês.
      setRefShortcut("months");
      setRefStart("");
      setRefLines(referenceLines(reference));
      return;
    }
    const storedByTarget = chosenTarget?.ruleId ? rulesByRuleId?.[chosenTarget.ruleId] ?? null : null;
    const storedCandidates = [referenceRule, storedByTarget].filter(
      (rule) => rule?.basis === "reference" && rule?.target?.key === chosenTarget?.key,
    );
    const stored = firstSeed ? storedCandidates[0] ?? null : null;
    const storedProfile = stored?.profile ?? null;
    const weighted = Array.isArray(storedProfile) && storedProfile.some((line) => line?.weight !== undefined)
      ? storedProfile
      : Array.isArray(storedProfile) ? bpsLinesAsCents(storedProfile, reference.totalCents) : null;
    // Com dados programados, abre ajustando os meses da referência.
    setRefShortcut("months");
    setRefLines(weighted ? weightedLines(weighted, reference.totalCents) : referenceLines(reference));
  }, [open, monthMode, awaitingCurrent, referenceIsCurrent, reference, referenceKey, refSeed, referenceRule, rulesByRuleId, chosenTarget]);

  if (!open) return null;
  const executionComplete = Boolean(confirmedResult.current);
  const titleText = monthMode
    ? "Cronograma atual"
    : initialRule
      ? "Editar regra de desembolso"
      : "Nova regra de desembolso";
  // Para alvos com prévia, a ação principal só habilita com a prévia CALCULADA (ou com a
  // declaração de que a obra não tem origem utilizável para calcular).
  // Num alvo de EAP, salvar espera a verificação dos insumos com regra própria terminar
  // (D19): sem ela, a escolha entre manter e sobrescrever passaria despercebida. Uma falha
  // da verificação não trava — vale a precedência de hoje.
  const impactSettled = needsImpact
    ? impact.status === "ready" || impact.status === "unavailable"
    : !wantsImpact || impact.status !== "loading";
  const hasOverrides = impactOverrides.length > 0;
  const canOverride = !aiDraft;
  const decisionPending = plan.hasPending;
  // D21: o ajuste mês a mês sobre a referência. O início vale o primeiro mês da referência
  // quando o cliente não escolheu; o "Personalizado" só é editável quando a referência cabe
  // nas linhas do perfil (senão vale o deslocamento e os atalhos).
  const refEffectiveStart = refStart || reference.first || null;
  const customEditable = fitsCustom(reference);
  const refLinesReady = monthMode && Array.isArray(refLines) && refSeed === referenceKey;
  // "Ajustar meses" e "Personalizado" são duas vistas das mesmas linhas em centavos.
  const refCustomLike = refShortcut === "custom" || refShortcut === "months";
  const refShortcutProfile = (() => {
    if (!monthMode || refCustomLike) return { profile: null, error: null };
    try {
      return { profile: validateProfile(buildShortcutProfile(refShortcut, refForm)), error: null };
    } catch (error) {
      return { profile: null, error: profileErrorText(error) };
    }
  })();
  const refBalance = refLinesReady && refCustomLike ? linesTotal(refLines) - reference.totalCents : 0;
  const refProblem = (() => {
    if (!refLinesReady) return null;
    if (!refCustomLike) return refShortcutProfile.error;
    return customEditable ? linesProblem(refLines) : null;
  })();
  const refSave = refLinesReady && !refProblem && refBalance === 0
    ? referenceSaveOf({
      // Sem regra "com base" (revisão de 2026-10-07): grava sempre o valor de cada mês, nunca
      // um deslocamento da origem.
      allowShift: false,
      shortcut: refCustomLike ? "custom" : refShortcut,
      lines: refLines,
      reference,
      shortcutProfile: refShortcutProfile.profile,
    })
    : null;
  // O ajuste igual à referência não muda nada: o início e as parcelas são os da origem.
  const refUnchanged = refLinesReady && refCustomLike && sameAsReference(refLines, reference)
    && refEffectiveStart === reference.first;
  const canSave = monthMode
    ? !!chosenTarget && !!refSave && !refUnchanged && !saving && impactSettled && !decisionPending
    : !isReadOnly &&
      !!chosenTarget &&
      !!validation.profile &&
      !saving &&
      impactSettled &&
      !decisionPending;
  const impactedCount = impact.answer?.totals?.impactedCount ?? 0;
  const missing = (() => {
    if (isReadOnly || saving || executionComplete || !chosenTarget || canSave) return null;
    if (monthMode) {
      if (previewHydration === "loading" || !refLinesReady) {
        return reference.first === null && previewHydration !== "loading"
          ? "Este alvo não tem valor na origem para ajustar."
          : "Carregando a referência do alvo.";
      }
      if (refProblem) return refProblem;
      if (refBalance !== 0) return "O saldo a redistribuir precisa fechar em zero.";
      if (refUnchanged) return "O ajuste está igual à referência: mude o início ou alguma parcela.";
    }
    if (!monthMode && !validation.profile) return "A forma de desembolso ainda não está completa e válida.";
    if (decisionPending) return pendingDecisionText(plan);
    if (!needsImpact)
      return "Verificando os insumos com regra própria neste alvo.";
    if (impact.status === "error") return "Calcule o impacto novamente para reprogramar.";
    return "Aguardando o cálculo do impacto nos serviços.";
  })();
  const originName = origin?.id ? ORIGIN_NAMES[origin.id] : null;
  // A série de referência do ajuste mês a mês é nomeada pela origem (D21).
  const monthReferenceLabel = referenceIsCurrent
    ? "Cronograma atual"
    : keepOrigin
      ? `${originName ?? "Origem"} sem os insumos com regra própria`
      : originName ?? "Origem";
  // Os ajustes de mês do PRÓPRIO alvo (recorte do gráfico ou o mês a mês anterior à D21):
  // qualquer regra salva no alvo os descarta.
  const targetAdjustments = impact.status === "ready"
    ? impact.answer?.targetAdjustments ?? null
    : null;
  const descendantAdjustedNodes = !monthMode && impact.status === "ready"
    ? impact.answer?.descendantAdjustedNodes ?? 0
    : 0;
  // O alvo já tem regra no OUTRO modo: salvar aqui a substitui (D21, uma regra por alvo).
  const existingRule = initialRule?.target?.key === chosenTarget?.key
    ? initialRule
    : chosenTarget?.ruleId ? rulesByRuleId?.[chosenTarget.ruleId] ?? null : null;
  const existingBasis = existingRule && existingRule.active !== false ? existingRule.basis ?? "new" : null;
  // Ajustar os meses do cronograma atual de uma regra é ajustá-la, não trocá-la.
  const replacesOtherMode = existingBasis !== null && !referenceIsCurrent
    && existingBasis !== (monthMode ? "reference" : "new");
  // Valor do item ajustado: o do alvo, ou o que a busca do previsto trouxe.
  const targetCents = chosenTarget
    ? chosenTarget.plannedCents ??
      (hydrated?.workId === workId && hydrated?.key === chosenTarget.key
        ? hydrated.plannedCents
        : null)
    : null;
  // O que a regra distribui de fato (D19): o valor do alvo sem as partes que continuam com
  // as regras mais específicas (preservadas ou mantidas); sobrescrevendo, aplicando ou com
  // "as duas", essas partes seguem esta regra.
  const keepCents = Number.isFinite(targetCents)
    ? Math.max(0, targetCents - plan.keptCents)
    : null;
  const ruleCents = hasOverrides ? keepCents : targetCents;
  // D4: com a prévia pronta, o resultante é o do servidor no cenário escolhido; o cálculo
  // local só responde enquanto ela carrega (ou quando a combinação escolhida não tem série).
  const scenarioSeries = impact.status === "ready" ? impact.answer?.targetSeries ?? {} : {};
  const resultingSeries = resultingSeriesOf(scenarioSeries, plan);
  const previewComparison = impact.status === "ready" ? impact.answer?.comparison : undefined;
  const comparisonLoading = wantsImpact && !isReadOnly && impact.status === "loading";

  // Espec. 4.1: escolher um alvo que JÁ tem regra carrega o perfil GRAVADO. Sobrescrever
  // em silêncio com o padrão à vista apagaria o contrato do cliente sem ele ver.
  const chooseTarget = (candidate) => {
    if (executionComplete) return;
    if (aiDraft && candidate?.key !== aiDraft.targetKey) {
      setSaveError("Descarte a proposta de IA antes de escolher outro alvo.");
      return;
    }
    setChosenTarget(candidate);
    setOverrideDecisions(EMPTY_OVERRIDE_DECISIONS);
    setFormTouched(false);
    setSaveError(null);
    setRuleSource("manual");
    const stored = candidate?.ruleId
      ? rulesByRuleId?.[candidate.ruleId] ?? null
      : null;
    const storedReference = stored?.basis === "reference";
    const storedActive = Boolean(stored) && stored.active !== false;
    setStartMonth(storedReference ? "" : stored?.startMonth ?? "");
    // D21: um alvo com ajuste mês a mês abre nele, com o início gravado; as parcelas são
    // semeadas quando a referência do alvo chega.
    setRefSeed(null);
    setRefLines(null);
    setRefStart(storedReference ? stored.startMonth ?? "" : "");
    setMode(storedReference || (storedActive && candidate?.kind === "node") ? EDITOR_MODES.monthEdit : null);
    if (stored?.profile && !storedReference) {
      setCustomLines(seedLines(stored.profile));
      setShortcut("custom");
      setDescription(stored.description ?? "");
      setOverwriteWarning(false);
    } else {
      // Alvo com regra cuja forma não está à mão: o aviso é a pista de que salvar
      // aqui substitui o que está gravado.
      setOverwriteWarning(Boolean(!storedReference && (candidate?.hasRule || candidate?.ruleId)));
    }
    onChangeTarget?.(candidate);
  };

  // Proposta da IA (espec. 10.3, `DescribeRuleWithAi`): preenche alvo E perfil de uma
  // vez. O atalho SEMPRE vira "Personalizado" — sem isso a lógica de atalho (`visible`,
  // acima) regeneraria o perfil semeado a partir do `form` no próximo render, e o
  // perfil que a IA propôs nunca chegaria a aparecer na tela.
  const applyAiProposal = ({
    target: aiTarget,
    profile: aiProfile,
    draftId,
    version,
  }) => {
    if (isReadOnly) return;
    if (executionComplete) return;
    if (
      typeof draftId !== "string" ||
      !draftId ||
      !Number.isSafeInteger(version) ||
      version < 1
    ) {
      setSaveError("A proposta não possui um rascunho válido. Gere novamente.");
      return;
    }
    const normalized = normalizeTarget(aiTarget);
    if (normalized?.key !== chosenTarget?.key) {
      setDescription("");
      setDescriptionDirty(false);
    }
    setAiDraft({ draftId, version, targetKey: normalized.key, workId });
    confirmationRequest.current = null;
    confirmedResult.current = null;
    setChosenTarget(normalized);
    setCustomLines(seedLines(aiProfile));
    setShortcut("custom");
    setRuleSource("ai_assistant");
    setSaveError(null);
    setOverwriteWarning(false);
    hydrationRequest.current = null;
    setHydrated(null);
    onChangeTarget?.(normalized);
  };

  const updateCustomLine = (lineId, patch) => {
    if (executionComplete) return;
    setRuleSource("manual");
    setCustomLines((lines) =>
      lines.map((line) =>
        line.lineId === lineId ? { ...line, ...patch } : line,
      ),
    );
  };

  const addCustomLine = () => {
    if (executionComplete) return;
    setRuleSource("manual");
    setCustomLines((lines) => {
      // Teto do validador (`MAX_LINES`): recusar aqui é melhor que gravar e ouvir 400.
      if (lines.length >= MAX_LINES) return lines;
      const last = lines[lines.length - 1];
      return [
        ...lines,
        withLineId({
          unit: last?.unit ?? "months",
          offset: (Number.isFinite(last?.offset) ? last.offset : 0) + 1,
          percentBps: 0,
        }),
      ];
    });
  };

  const pickShortcut = (value) => {
    if (executionComplete) return;
    setRuleSource("manual");
    // Entrar no "Personalizado" leva a tabela que está na tela, não o padrão à vista.
    if (value === "custom" && shortcut !== "custom")
      setCustomLines(seedLines(validation.profile));
    setShortcut(value);
  };

  const save = async () => {
    if (!canSave) return;
    const scope = saveScope.current;
    setSaving(true);
    setSaveError(null);
    try {
      const trimmedDescription = description.trim();
      const payload = {
        target: ruleTargetOf(chosenTarget),
        ...(monthMode
          ? {
              // Revisão de 2026-10-07: a regra é sempre um cronograma criado do zero. O
              // cronograma atual ajustado é gravado como o valor de cada mês (ou um atalho em
              // bps), a partir do início — sem "base" nenhuma.
              profile: refSave.profile,
              ...(refEffectiveStart ? { startMonth: refEffectiveStart } : {}),
            }
          : {
              profile: validation.profile,
              // O início efetivo (o escolhido ou o primeiro mês do alvo na origem): o que a
              // prévia mostrou é o que fica gravado.
              ...(effectiveStartMonth ? { startMonth: effectiveStartMonth } : {}),
            }),
        // Sempre manda `description`, mesmo vazia: é o único jeito de LIMPAR uma
        // descrição já gravada. O cliente da API já descarta apenas `undefined`,
        // então uma string vazia aqui grava vazio de propósito (não é omissão).
        description: trimmedDescription,
        // O carimbo das entradas em que a prévia foi calculada: se a obra mudou desde então
        // o servidor recusa (409) e a prévia é recalculada.
        ...(impact.answer?.previewStamp ? { previewStamp: impact.answer.previewStamp } : {}),
        // D19/D5: sobrescrever (EAP) ou aplicar (insumo) desativa as regras listadas.
        ...(plan.overrideRuleIds.length > 0 ? { overrideRuleIds: plan.overrideRuleIds } : {}),
        // D6: "as duas" — esta regra prevalece sobre as do insumo só dentro do alvo.
        ...(plan.prevailsOverRuleIds.length > 0
          ? { prevailsOverRuleIds: plan.prevailsOverRuleIds }
          : {}),
      };
      // Chamador que grava por conta (contrato da Task 16) passa `onSave`; sem ele o
      // editor fala com a rota. O envelope é conferido nos DOIS caminhos: delegar não
      // é motivo para fechar o editor sobre uma gravação que não aconteceu.
      let result;
      if (aiDraft) {
        if (
          aiDraft.workId !== workId ||
          chosenTarget.key !== aiDraft.targetKey
        ) {
          setSaveError(
            "O alvo mudou. Gere outra proposta ou descarte esta para editar manualmente.",
          );
          return;
        }
        const body = {
          expectedVersion: aiDraft.version,
          targetKey: chosenTarget.key,
          profile: validation.profile,
          ...(descriptionDirty ? { description: trimmedDescription } : {}),
        };
        const fingerprint = JSON.stringify([workId, aiDraft.draftId, body]);
        if (confirmationRequest.current?.fingerprint !== fingerprint) {
          confirmationRequest.current = {
            fingerprint,
            idempotencyKey: newIdempotencyKey(),
          };
        }
        if (confirmedResult.current) {
          if (confirmedResult.current.fingerprint !== fingerprint)
            throw new Error(
              "A regra já foi salva. Feche e abra uma nova edição para alterar o conteúdo.",
            );
          result = confirmedResult.current.result;
        } else {
          const receipt = await confirmRuleWithAi(
            workId,
            aiDraft.draftId,
            body,
            { idempotencyKey: confirmationRequest.current.idempotencyKey },
          );
          if (saveScope.current !== scope) return;
          if (
            receipt?.status !== "executed" ||
            receipt.draftId !== aiDraft.draftId ||
            receipt.targetKey !== body.targetKey ||
            receipt.version !== aiDraft.version + 1 ||
            typeof receipt.ruleId !== "string" ||
            !receipt.ruleId ||
            !Number.isSafeInteger(receipt.rulesRevision) ||
            receipt.rulesRevision < 0
          ) {
            throw new Error("invalid_execution_receipt");
          }
          result = {
            ...receipt,
            code: receipt.regeneration?.status === "ready" ? 200 : 202,
          };
          confirmedResult.current = { fingerprint, result };
        }
      } else {
        result = onSave
          ? await onSave(payload)
          : await saveDisbursementRule(workId, {
              ...payload,
              source: ruleSource,
            });
      }
      if (saveScope.current !== scope) return;
      if (!result || (result.code !== 200 && result.code !== 202)) {
        setSaveError(saveErrorText(result));
        if (result?.errorCode === "impact_preview_stale") impact.refresh();
        return;
      }
      await onSaved?.(result);
      if (saveScope.current === scope) onClose?.();
    } catch (error) {
      // O `onSave` do chamador pode rejeitar (rede, 4xx/5xx): virar mensagem na tela
      // em vez de rejeição não tratada, e o botão volta a aceitar clique.
      if (saveScope.current === scope)
        setSaveError(
          aiDraft && error?.message !== "invalid_execution_receipt"
            ? error?.message ?? SAVE_ERROR_TEXT
            : SAVE_ERROR_TEXT,
        );
    } finally {
      if (saveScope.current === scope) setSaving(false);
    }
  };

  // D22: "Remover regra" — desativa com histórico e tira do bloco; o alvo volta a seguir o
  // que governa ele sem esta regra.
  const removeRule = async () => {
    if (!existingRule?.ruleId || removing || isReadOnly) return;
    const scope = saveScope.current;
    setRemoving(true);
    setSaveError(null);
    try {
      const result = await removeDisbursementRule(workId, existingRule.ruleId);
      if (saveScope.current !== scope) return;
      if (!result || (result.code !== 200 && result.code !== 202)) {
        const detail = disbursementErrorDetail(result?.errorCode);
        setSaveError(detail ? `Não foi possível remover a regra. ${detail}` : REMOVE_ERROR_TEXT);
        return;
      }
      await onSaved?.(result);
      if (saveScope.current === scope) onClose?.();
    } catch {
      if (saveScope.current === scope) setSaveError(REMOVE_ERROR_TEXT);
    } finally {
      if (saveScope.current === scope) setRemoving(false);
    }
  };

  const pickMode = (value) => {
    if (executionComplete || value === effectiveMode) return;
    setMode(value);
    setSaveError(null);
    setConfirmingRemove(false);
  };

  // D21: atalhos do ajuste sobre a referência. Entrar num atalho abre o formulário com a
  // janela da referência; o "Personalizado" volta com as parcelas que estavam na tela.
  const pickReferenceShortcut = (value) => {
    if (executionComplete || value === refShortcut) return;
    if (value !== "custom") setRefForm(referenceForm(value, reference.span));
    setRefShortcut(value);
    setSaveError(null);
  };
  const backToReference = () => {
    if (!refCustomLike) setRefShortcut("months");
    setRefStart("");
    setRefLines(referenceLines(reference));
    setSaveError(null);
  };

  const inherited = chosenTarget?.inheritedFrom ?? null;
  const inheritedLabel =
    inherited && typeof inherited === "object"
      ? inherited.label ?? inherited.key
      : inherited;
  const startHint = (() => {
    if (startMonth) return "Competência da 1ª parcela (defasagem 0)";
    if (firstOriginMonth)
      return `Padrão: 1º mês do alvo na origem (${competenceText(firstOriginMonth)})`;
    if (targetUnscheduled > 0)
      return "O orçamento do alvo ainda não tem distribuição: o padrão é o mês atual";
    return "Padrão: 1º mês do alvo na origem";
  })();
  const canEditForm = !isReadOnly && !executionComplete;
  // D22: remover a regra que o editor abriu (ativa ou inativa).
  const canRemove = Boolean(existingRule?.ruleId) && !isReadOnly && !aiDraft && !executionComplete;
  const removeConsequence = chosenTarget?.kind === "node"
    ? `Remover a regra de "${chosenTarget?.label ?? ""}"? O alvo volta a seguir a regra de um nível acima, se houver, ou ${origin?.id ? ORIGIN_FOLLOWS_OBJECT[origin.id] : "a origem"}.`
    : `Remover a regra de "${chosenTarget?.label ?? ""}"? Cada serviço volta a seguir a sua regra de EAP ou a origem.`;
  // O ajuste mês a mês é gravado pela rota do módulo; a proposta da IA confirma uma REGRA.
  const offersModes = nodeTarget && canEditForm && !aiDraft;
  const modeSwitch = offersModes ? (
    <div
      className="ampli-rule-editor__shortcuts ampli-rule-editor__modes"
      role="group"
      aria-label="Como ajustar o desembolso do alvo"
    >
      {[
        [EDITOR_MODES.schedule, "Novo cronograma"],
        [EDITOR_MODES.monthEdit, "Cronograma atual"],
      ].map(([value, label]) => (
        <button
          key={value}
          type="button"
          aria-pressed={effectiveMode === value}
          className={effectiveMode === value ? "active" : undefined}
          onClick={() => pickMode(value)}
        >
          {label}
        </button>
      ))}
    </div>
  ) : null;
  // D21: o que muda ao salvar, dito antes — nos dois modos.
  const modeWarnings = (
    <>
      {replacesOtherMode ? (
        <p className="alert alert-info" role="status" data-testid="replaces-other-mode">
          {monthMode
            ? "Este alvo tem um novo cronograma. Salvar o ajuste mês a mês o substitui."
            : "Este alvo tem ajuste mês a mês. Salvar um novo cronograma o substitui."}
        </p>
      ) : null}
      {monthMode && inheritedLabel ? (
        <p className="alert alert-info" role="status" data-testid="ancestor-rule-stops">
          {`A regra de ${inheritedLabel} deixa de valer para este alvo depois de salvar: ele passa a seguir o ajuste da referência.`}
        </p>
      ) : null}
      {targetAdjustments?.monthCount > 0 ? (
        <p className="alert alert-info" role="status" data-testid="rule-discards-month-edit">
          {`Este alvo tem ajustes de mês em ${monthsText(targetAdjustments.monthCount)}. Salvar descarta esses ajustes.`}
        </p>
      ) : null}
    </>
  );
  // A referência gravada no ajuste não é mais a de hoje (a origem recarregou).
  const referenceChanged = monthMode
    && existingBasis === "reference"
    && Number.isSafeInteger(existingRule?.reference?.totalCents)
    && originReference.first !== null
    && existingRule.reference.totalCents !== originReference.totalCents;
  const referencePoints = [...reference.map].map(([month, cents]) => ({ month, cents }));
  // O ajustado do "Personalizado" é exatamente o que as linhas dizem, mesmo com saldo aberto.
  const customResultingSeries = (() => {
    if (!refLinesReady || !refEffectiveStart) return null;
    const byMonth = new Map();
    for (const line of refLines) {
      const month = lineMonth(refEffectiveStart, line);
      if (month && line.cents > 0) byMonth.set(month, (byMonth.get(month) ?? 0) + line.cents);
    }
    return [...byMonth];
  })();
  const refShortcutLines = (refShortcutProfile.profile ?? []).map((line) => withLineId(line));
  const refStartField = (
    <label className="ampli-rule-editor__field ampli-rule-editor__field--start">
      <span className="card-label">Início</span>
      <CompetenceInput
        aria-label="Início do ajuste"
        aria-describedby="ampli-rule-editor-ref-start-hint"
        value={refEffectiveStart ?? ""}
        disabled={!canEditForm}
        onChange={(competence) => {
          if (!canEditForm) return;
          setRefStart(competence);
          setSaveError(null);
        }}
      />
      <span className="ampli-rule-editor__hint" id="ampli-rule-editor-ref-start-hint">
        {reference.first
          ? `1º mês da referência: ${competenceText(reference.first)}. Mudar o início desloca todas as parcelas.`
          : "Mudar o início desloca todas as parcelas."}
      </span>
    </label>
  );
  const startField = (
    <label className="ampli-rule-editor__field ampli-rule-editor__field--start">
      <span className="card-label">Início do novo cronograma</span>
      <CompetenceInput
        aria-label="Início do novo cronograma"
        aria-describedby="ampli-rule-editor-start-hint"
        value={effectiveStartMonth ?? ""}
        disabled={!canEditForm}
        onChange={(competence) => {
          if (!canEditForm) return;
          setRuleSource("manual");
          setStartMonth(competence);
        }}
      />
      <span className="ampli-rule-editor__hint" id="ampli-rule-editor-start-hint">
        {startHint}
      </span>
    </label>
  );

  return (
    <div className="offcanvas active">
      <div
        className="offcanvas-container large ampli-rule-editor"
        role="dialog"
        aria-modal="true"
        aria-label={titleText}
        tabIndex={-1}
        ref={panelRef}
      >
        <hgroup className="offcanvas-header ampli-rule-editor__header">
          <div>
            <h2>{titleText}</h2>
            <p className="ampli-rule-editor__lead">
              {monthMode
                ? "O ajuste parte do cronograma da origem do alvo: mude o início para deslocar a programação inteira ou ajuste as parcelas. O total do alvo não muda."
                : "A regra substitui o cronograma do alvo: o valor global dele é redistribuído por completo a partir do início escolhido, e os itens abaixo seguem a mesma forma até que um alvo mais específico a sobrescreva."}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar">
            <i
              className="icon icon-width-24 icon-times background-accent"
              aria-hidden={true}
            ></i>
          </button>
        </hgroup>

        <div className="offcanvas-content ampli-rule-editor__content">
          {!chosenTarget ? (
            <RuleTargetPicker
              targetType={targetType}
              nodeLevel={nodeLevel}
              search={search}
              candidates={candidates}
              loading={loadingTargets}
              error={targetsError}
              onChangeTargetType={setTargetType}
              onChangeNodeLevel={setNodeLevel}
              onChangeSearch={setSearch}
              onChoose={chooseTarget}
            />
          ) : (
            <section aria-label="Forma de desembolso" className="ampli-rule-editor__steps">
              <section className="ampli-rule-editor__section">
                <h3>Alvo</h3>
                <div className="ampli-rule-editor__chosen">
                  <div className="ampli-rule-editor__chosen-main">
                    <strong className="ampli-rule-editor__target-label">
                      {chosenTarget.label}
                    </strong>
                    {chosenTarget.path ? (
                      <span className="ampli-rule-editor__target-path">
                        {chosenTarget.path}
                      </span>
                    ) : null}
                    {inheritedLabel ? (
                      <span className="ampli-rule-editor__inherited">
                        {`herdado de ${inheritedLabel}`}
                      </span>
                    ) : null}
                  </div>
                  <dl className="ampli-rule-editor__facts">
                    <div>
                      <dt>Valor global</dt>
                      <dd>
                        <TargetValue cents={targetCents} />
                      </dd>
                    </div>
                    {originName ? (
                      <div>
                        <dt>Origem vigente</dt>
                        <dd data-testid="rule-origin">{originName}</dd>
                      </div>
                    ) : null}
                  </dl>
                  {(!seededTarget && !executionComplete) ||
                  (aiDraft && !executionComplete) ? (
                    <div className="ampli-rule-editor__chosen-actions">
                      {!seededTarget && !executionComplete ? (
                        <button
                          type="button"
                          className="button button-outline small"
                          onClick={() => chooseTarget(null)}
                        >
                          Trocar alvo
                        </button>
                      ) : null}
                      {aiDraft && !executionComplete ? (
                        <button
                          type="button"
                          className="button button-ghost small"
                          disabled={saving}
                          onClick={() => {
                            setAiDraft(null);
                            setRuleSource("manual");
                            setSaveError(null);
                            confirmationRequest.current = null;
                            confirmedResult.current = null;
                          }}
                        >
                          Descartar proposta e editar manualmente
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                {overwriteWarning ? (
                  <p className="alert alert-info" role="status">
                    Este alvo já tem regra: salvar aqui substitui a forma atual.
                  </p>
                ) : null}
              </section>

              {/* D19: antes da forma, porque o valor que a forma divide depende da escolha. */}
              <RuleOverridesSection
                impact={wantsImpact && !isReadOnly ? impact : null}
                onEditOverride={
                  onEditOverride && !executionComplete
                    ? (override) => onEditOverride(override)
                    : undefined
                }
                decisions={overrideDecisions}
                onDecide={
                  executionComplete
                    ? undefined
                    : (group, value) => {
                        setOverrideDecisions((current) => ({ ...current, [group]: value }));
                        setSaveError(null);
                      }
                }
                targetCents={targetCents ?? 0}
                canOverride={canOverride}
              />

              {monthMode ? (
                <>
                  <section className="ampli-rule-editor__section">
                    <h3>Como esse alvo é pago?</h3>
                    {modeSwitch}
                    {modeWarnings}
                    <p className="ampli-rule-editor__section-lead">
                      {`Parte ${referenceIsCurrent ? "do cronograma atual do alvo (o que a regra produz hoje)" : `de ${originName ? `"${originName}"` : "a origem do alvo"}`}. Mude o início e a programação inteira anda junto, ou ajuste as parcelas: no personalizado, o valor de cada mês; nos atalhos, parcelas iguais ou entrada + parcelas sobre o valor do alvo.`}
                    </p>
                    {!refLinesReady ? (
                      <p className="card-label" role="status">
                        {reference.first === null && previewHydration !== "loading"
                          ? previewHydration === "failed"
                            ? "Não foi possível carregar a origem do alvo."
                            : "Este alvo não tem valor na origem para ajustar."
                          : "Carregando a referência do alvo…"}
                      </p>
                    ) : (
                      <>
                        {referenceChanged ? (
                          <p className="alert alert-info" role="status" data-testid="reference-changed">
                            {`A referência mudou desde o ajuste: a origem do alvo era ${reais(existingRule.reference.totalCents)} e agora é ${reais(originReference.totalCents)}. As parcelas foram reescaladas para o valor de hoje.`}
                          </p>
                        ) : null}
                        <RuleShortcutFields
                          shortcuts={REFERENCE_SHORTCUTS}
                          shortcut={refShortcut}
                          form={refForm}
                          errorId={refProblem ? PROFILE_ERROR_ID : undefined}
                          onPick={pickReferenceShortcut}
                          onPatch={(patch) => {
                            setRefForm((current) => ({ ...current, ...patch }));
                            setSaveError(null);
                          }}
                          startField={refStartField}
                        />
                        {refCustomLike && !customEditable ? (
                          <p className="alert alert-info" role="status" data-testid="reference-too-long">
                            {`A referência tem ${reference.span} meses e o cronograma aceita até ${WEIGHTED_MAX_OFFSET + 1}. Mude o início para deslocar a programação inteira, ou use parcelas iguais ou entrada + parcelas.`}
                          </p>
                        ) : null}
                        {refShortcut === "months" ? (
                          <ReferenceMonthsTable
                            reference={reference}
                            lines={refLines}
                            startMonth={refEffectiveStart}
                            balanceCents={refBalance}
                            referenceLabel={monthReferenceLabel}
                            editable={canEditForm && customEditable}
                            problem={refProblem}
                            onChangeLines={(next) => {
                              setRefLines(next);
                              setSaveError(null);
                            }}
                            onBackToReference={backToReference}
                            backToReferenceDisabled={refUnchanged}
                          />
                        ) : refShortcut === "custom" ? (
                          <>
                            <ReferenceInstallmentsTable
                              lines={refLines}
                              startMonth={refEffectiveStart}
                              totalCents={reference.totalCents}
                              balanceCents={refBalance}
                              editable={canEditForm && customEditable}
                              problem={refProblem}
                              onChangeCents={(lineId, cents) => {
                                setRefLines((lines) => lines.map((line) => (line.lineId === lineId ? { ...line, cents } : line)));
                                setSaveError(null);
                              }}
                              onPostpone={(lineId) => setRefLines((lines) => postponeLine(lines, lineId))}
                              onRemove={(lineId) => setRefLines((lines) => lines.filter((line) => line.lineId !== lineId))}
                              onAdd={() => setRefLines((lines) => addLine(lines))}
                              onBackToReference={backToReference}
                              backToReferenceDisabled={refUnchanged}
                            />
                          </>
                        ) : (
                          <RuleProfileTable
                            lines={refShortcutLines}
                            editable={false}
                            message={refProblem}
                            summary={refShortcutProfile.profile ? describeProfile(refShortcutProfile.profile) : null}
                            totalCents={reference.totalCents}
                            startMonth={refEffectiveStart}
                          />
                        )}
                      </>
                    )}
                  </section>
                  {refLinesReady ? (
                    <section className="ampli-rule-editor__section">
                      <RulePreviewChart
                        plannedSeries={referencePoints}
                        basis="reference"
                        profile={refCustomLike ? null : refShortcutProfile.profile ?? []}
                        startMonth={refEffectiveStart}
                        resultingSeries={refCustomLike ? customResultingSeries : null}
                        originName={monthReferenceLabel}
                        targetLabel={chosenTarget.label}
                        resultingLabel="Ajustado"
                      />
                    </section>
                  ) : null}
                </>
              ) : null}

              {!monthMode ? (
              <section className="ampli-rule-editor__section">
                <h3>Como esse alvo é pago?</h3>
                {modeSwitch}
                {modeWarnings}
                {descendantAdjustedNodes > 0 ? (
                  <p className="card-label" role="status" data-testid="rule-keeps-descendant-month-edits">
                    {`${descendantAdjustedNodes === 1 ? "Um item" : `${descendantAdjustedNodes} itens`} abaixo deste alvo ${descendantAdjustedNodes === 1 ? "tem" : "têm"} ajuste mês a mês. ${descendantAdjustedNodes === 1 ? "Ele continua" : "Eles continuam"} valendo e o que não couber na nova curva aparece como excedente.`}
                  </p>
                ) : null}
                <p className="ampli-rule-editor__section-lead">
                  Escolha o início e a forma. O valor global sai dos meses da
                  origem e é dividido pelas parcelas abaixo; num alvo com vários
                  itens, cada um recebe a mesma forma na proporção do seu valor.
                </p>

                {canEditForm ? (
                  <RuleShortcutFields
                    shortcut={shortcut}
                    form={scheduleForm}
                    errorId={validation.message ? PROFILE_ERROR_ID : undefined}
                    onPick={pickShortcut}
                    onPatch={(patch) => {
                      setRuleSource("manual");
                      setForm({ ...scheduleForm, ...patch });
                      setFormTouched(true);
                    }}
                    startField={startField}
                  />
                ) : (
                  <div className="ampli-rule-editor__shortcut-fields">{startField}</div>
                )}

                <RuleProfileTable
                  lines={visible.lines}
                  editable={canEditForm && shortcut === "custom"}
                  message={validation.message}
                  summary={validation.summary}
                  onChangeLine={updateCustomLine}
                  onRemoveLine={(lineId) => {
                    setRuleSource("manual");
                    setCustomLines((lines) =>
                      lines.filter((line) => line.lineId !== lineId),
                    );
                  }}
                  onAddLine={addCustomLine}
                  totalCents={ruleCents}
                  showZeroTotal={hasOverrides}
                  startMonth={effectiveStartMonth}
                />
              </section>
              ) : null}

              {needsImpact && !monthMode ? (
                <section className="ampli-rule-editor__section">
                  <RuleImpactSection
                    impact={impact}
                    readOnly={isReadOnly}
                    onRetry={impact.refresh}
                  />
                </section>
              ) : null}

              {!monthMode ? (
              <section className="ampli-rule-editor__section">
                {/* O título mora no gráfico: é ele que sabe se a obra está em R$ ou CUB. */}
                <RulePreviewChart
                  plannedSeries={previewSeries}
                  unscheduledCents={targetUnscheduled}
                  profile={validation.profile ?? visible.lines}
                  startMonth={effectiveStartMonth}
                  hydration={previewHydration}
                  originName={originName}
                  targetLabel={chosenTarget.label}
                  resultingSeries={resultingSeries}
                  comparison={previewComparison}
                  comparisonLoading={comparisonLoading}
                />
              </section>
              ) : null}

              {!isReadOnly ? (
                <section className="ampli-rule-editor__section">
                  <h3>{monthMode ? "Descrição" : "Descrição e IA"}</h3>
                  <label className="ampli-rule-editor__field">
                    <span className="card-label">Descrição (opcional)</span>
                    <input
                      type="text"
                      className="default-input"
                      aria-label="Descrição"
                      value={description}
                      maxLength={500}
                      disabled={executionComplete}
                      onChange={(event) => {
                        if (executionComplete) return;
                        setDescription(event.target.value);
                        setDescriptionDirty(true);
                      }}
                      placeholder="Contrato padrão de elevadores"
                    />
                  </label>

                  {/* Espec. 10.3: texto livre vira PROPOSTA de alvo + perfil; nada é
                      gravado aqui — só o botão principal do rodapé grava. */}
                  {!saving && !executionComplete && !monthMode ? (
                    <DescribeRuleWithAi
                      workId={workId}
                      targetKey={chosenTarget.key}
                      onFill={applyAiProposal}
                    />
                  ) : null}
                </section>
              ) : null}

              {saveError ? (
                <p className="alert alert-error" role="alert">
                  {saveError}
                </p>
              ) : null}
              {executionComplete ? (
                <p role="status">
                  A regra já foi salva. Atualize a tela para concluir; para
                  alterar o conteúdo, feche e abra uma nova edição.
                </p>
              ) : null}
            </section>
          )}
        </div>
        {chosenTarget ? (
          <footer className="ampli-rule-editor__footer">
            {missing ? (
              <p className="card-label ampli-rule-editor__missing" role="status">
                {missing}
              </p>
            ) : null}
            <button
              type="button"
              className="button button-ghost small"
              onClick={onClose}
            >
              {isReadOnly ? "Fechar" : "Cancelar"}
            </button>
            {canRemove ? (
              confirmingRemove ? (
                <div
                  className="ampli-rule-editor__remove-confirm"
                  role="group"
                  aria-label="Confirmar a remoção da regra"
                >
                  <p className="card-label" role="status">{removeConsequence}</p>
                  <button
                    type="button"
                    className="button button-ghost small"
                    disabled={removing}
                    onClick={() => setConfirmingRemove(false)}
                  >
                    Manter regra
                  </button>
                  <button
                    type="button"
                    className="button button-error small"
                    disabled={removing}
                    onClick={removeRule}
                  >
                    {removing ? "Removendo…" : "Confirmar remoção"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="button button-error small"
                  disabled={saving}
                  onClick={() => {
                    setConfirmingRemove(true);
                    setSaveError(null);
                  }}
                >
                  Remover regra
                </button>
              )
            ) : null}
            {!isReadOnly ? (
              <button
                type="button"
                className="button button-accent small"
                disabled={!canSave || removing}
                onClick={save}
              >
                {executionComplete
                  ? "Atualizar tela"
                  : saving
                    ? "Salvando…"
                    : monthMode
                      ? "Salvar ajuste"
                      : impactedCount > 0
                        ? "Reprogramar desembolso"
                        : "Salvar regra"}
              </button>
            ) : null}
          </footer>
        ) : null}
      </div>
      <div onClick={onClose} className="offcanvas-background" />
    </div>
  );
};

export default RuleEditorOffcanvas;
